import { expect, test as vitestTest } from 'vitest';
import { z } from 'zod';

// 💡 EXPORTS
export { z as s }; // Export zod aliased as 's' (for schema)

interface TestContext {
  request: SuperApiClient;
}

/**
 * 🛠️ CUSTOM VITEST WRAPPER PIPELINE
 * Injects the optimized framework client instance directly into the test execution context.
 */
export const apiTest = (name: string, fn: (context: TestContext) => Promise<void>) => {
  vitestTest(name, async () => {
    // Modify this default base URL fallback as required by your project configurations
    const client = new SuperApiClient('https://api.restful-api.dev'); 
    await fn({ request: client });
  });
};

/**
 * 📦 Unified HTTP Telemetry Response Structural Wrapper
 */
export class ApiResponseAssertion {
  public status: number;
  public statusText: string;
  public headers: Record<string, string>;
  public body: any;
  public responseTimeMs: number;

  constructor(rawResponse: any, duration: number) {
    this.status = rawResponse.status;
    this.statusText = rawResponse.statusText;
    this.headers = rawResponse.headers || {};
    this.body = rawResponse.body;
    this.responseTimeMs = duration;
  }

  /**
   * 🎯 Fluent assertion API to validate response status codes
   */
  public expectStatus(expectedCode: number): this {
    expect(this.status).toBe(expectedCode);
    return this;
  }
}

/**
 * 🚀 Central Orchestration Client Factory Engine
 */
export class SuperApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  /**
   * Universal Request Multiplexer
   */
  public async send(url: string, method: string, options: any = {}): Promise<ApiResponseAssertion> {
    // Construct absolute target paths seamlessly whether an explicit URL or a suffix stub is passed
    const fullUrl = url.startsWith('http://') || url.startsWith('https://') 
      ? url 
      : `${this.baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

    const fetchOptions: RequestInit = {
      method: method.toUpperCase(),
      headers: { ...options.headers }
    };

    // 🌟 FIX: Safely assert headers as a indexable string Record to clear the compiler error
    if (options.body && ['POST', 'PUT', 'PATCH'].includes(fetchOptions.method!)) {
      const headers = fetchOptions.headers as Record<string, string>;
      if (!headers['content-type']) {
        headers['content-type'] = 'application/json';
      }
      fetchOptions.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    }

    const startTime = Date.now();
    try {
      const response = await fetch(fullUrl, fetchOptions);
      const duration = Date.now() - startTime;

      let parsedBody: any = null;
      const textPayload = await response.text();
      try {
        parsedBody = JSON.parse(textPayload);
      } catch {
        parsedBody = textPayload;
      }

      const rawTelemetry = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: parsedBody
      };

      return new ApiResponseAssertion(rawTelemetry, duration);
    } catch (error: any) {
      const duration = Date.now() - startTime;
      const errorTelemetry = {
        status: 500,
        statusText: 'Internal SDK Networking Exception',
        headers: {},
        body: { error: error.message }
      };
      return new ApiResponseAssertion(errorTelemetry, duration);
    }
  }

  // 📡 Direct Shortcuts
  public async get(url: string, options?: any) { return this.send(url, 'GET', options); }
  public async post(url: string, options?: any) { return this.send(url, 'POST', options); }
  public async put(url: string, options?: any) { return this.send(url, 'PUT', options); }
  public async patch(url: string, options?: any) { return this.send(url, 'PATCH', options); }
  public async delete(url: string, options?: any) { return this.send(url, 'DELETE', options); }
}